import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { ConfirmationService } from '../confirmation-modal/confirmation.service';
import { ToastService } from '../toast/toast.service';
import { AiSystemPromptService, ImprovePromptContext } from '../../services/ai-system-prompt.service';

/**
 * Os dois botões que acompanham todo campo de prompt: melhorar com IA e restaurar o padrão
 * do sistema. Fica no shared porque aparece no agente, nas listas e nas sequências.
 *
 * Não escreve no formulário: emite o texto novo e quem hospeda decide onde colar — assim serve
 * tanto para ReactiveForms quanto para ngModel.
 */
@Component({
  selector: 'app-prompt-actions',
  templateUrl: './prompt-actions.component.html',
  styleUrls: ['./prompt-actions.component.scss'],
})
export class PromptActionsComponent implements OnDestroy {

  /** Que campo é este — muda a instrução dada ao modelo e qual padrão é restaurado. */
  @Input() context: ImprovePromptContext = 'agent_base';

  /** Texto atual do campo. */
  @Input() prompt = '';

  /** Agente dono do campo. Sem ele não há como cobrar a melhoria, então o botão fica off. */
  @Input() agentId: string | null = null;

  /** Só no contexto 'event': de qual evento vem o padrão. */
  @Input() platformEventId: number | string | null = null;

  /** Esconde o "restaurar padrão" onde ele não faz sentido. */
  @Input() showRestore = true;

  @Output() promptChange = new EventEmitter<string>();

  isImproving = false;
  isRestoring = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private aiSystemPromptService: AiSystemPromptService,
    private confirmationService: ConfirmationService,
    private toastService: ToastService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get canImprove(): boolean {
    return !!this.agentId && (this.prompt || '').trim().length >= 10 && !this.isImproving;
  }

  get improveTooltip(): string {
    if (!this.agentId) {
      return 'Salve o agente antes de usar a melhoria com IA.';
    }
    if ((this.prompt || '').trim().length < 10) {
      return 'Escreva um pouco mais antes de pedir a melhoria.';
    }
    return 'A IA reescreve o que você escreveu. Consome tokens do seu saldo.';
  }

  improve(): void {
    if (!this.canImprove) {
      return;
    }

    this.isImproving = true;
    this.subscriptions.push(
      this.aiSystemPromptService.improve(this.agentId, this.context, this.prompt).subscribe({
        next: (result) => {
          this.isImproving = false;
          this.promptChange.emit(result.prompt);
          this.toastService.success('Prompt reescrito. Revise antes de salvar.');
        },
        error: (res) => {
          this.isImproving = false;
          this.toastService.error(res?.error?.message || 'Erro ao melhorar o prompt.');
        },
      })
    );
  }

  /** Sobrescreve o que o cliente escreveu, então confirma antes. */
  restore(): void {
    if (this.isRestoring) {
      return;
    }

    const apply = () => {
      this.isRestoring = true;
      this.subscriptions.push(
        this.aiSystemPromptService.defaults(this.platformEventId).subscribe({
          next: (defaults) => {
            this.isRestoring = false;
            const text = this.pick(defaults);

            if (!text) {
              this.toastService.info('Este campo não tem um texto padrão do sistema.');
              return;
            }

            this.promptChange.emit(text);
            this.toastService.success('Texto padrão carregado. Salve para aplicar.');
          },
          error: () => {
            this.isRestoring = false;
            this.toastService.error('Erro ao carregar o texto padrão.');
          },
        })
      );
    };

    if ((this.prompt || '').trim()) {
      this.confirmationService.show(
        'Restaurar o texto padrão do sistema <strong>substitui o que você escreveu</strong> neste campo. Continuar?',
        apply,
      );
      return;
    }

    apply();
  }

  private pick(defaults: { agent_base: string; manual_list: string; event: string | null }): string {
    if (this.context === 'event') {
      return defaults.event || '';
    }
    if (this.context === 'manual_list') {
      return defaults.manual_list || '';
    }
    return defaults.agent_base || '';
  }
}
